import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DashboardLayout from '../layout/DashboardLayout';
import { fetchSafetyContent, uploadSafetyContent, createFolder, deleteSafetyContent, deleteFolder, setCurrentFolder, updateBreadcrumbs } from '../../store/slices/safetySlice';
import { closeGreyMenu } from '../../store/slices/menuSlice';
import Spinner from '../common/Spinner';
import EmptyState from '../common/EmptyState';
import Modal from '../common/Modal';
import Button from '../common/Button';

const Safety = () => {
  const dispatch = useDispatch();
  const { content, folders, loading, currentFolder, breadcrumbs } = useSelector(state => state.safety);
  const { user } = useSelector(state => state.auth);
  const isAdmin = user && (user.profile.role === 'admin' || user.profile.role === 'super_admin');
  
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [fileToUpload, setFileToUpload] = useState(null);
  const [fileTitle, setFileTitle] = useState('');
  const [fileDescription, setFileDescription] = useState('');
  const [folderName, setFolderName] = useState('');
  const [itemToDelete, setItemToDelete] = useState({ id: null, type: null });
  const [previewItem, setPreviewItem] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const videoRef = useRef(null);
  
  useEffect(() => {
    dispatch(fetchSafetyContent(currentFolder));
  }, [dispatch, currentFolder]);
  
  // Ensure GreyMenu is closed when Safety component mounts
  useEffect(() => {
    dispatch(closeGreyMenu());
  }, [dispatch]);
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileToUpload(file);
      setFileTitle(file.name.split('.')[0]); // Set default title as filename
    }
  };
  
  const handleUpload = async () => {
    const formData = new FormData();
    formData.append('file', fileToUpload);
    formData.append('title', fileTitle);
    formData.append('description', fileDescription);
    formData.append('folder', currentFolder);
    
    await dispatch(uploadSafetyContent(formData));
    setIsUploadModalOpen(false);
    resetUploadForm();
  };
  
  const resetUploadForm = () => {
    setFileToUpload(null);
    setFileTitle('');
    setFileDescription('');
  };
  
  const handleCreateFolder = async () => {
    if (folderName.trim() === '') return;
    
    await dispatch(createFolder({ name: folderName, parentFolder: currentFolder }));
    setIsFolderModalOpen(false);
    setFolderName('');
  };
  
  const handleDelete = async () => {
    if (itemToDelete.type === 'content') {
      await dispatch(deleteSafetyContent(itemToDelete.id));
    } else if (itemToDelete.type === 'folder') {
      await dispatch(deleteFolder(itemToDelete.id));
    }
    setIsDeleteModalOpen(false);
    setItemToDelete({ id: null, type: null });
  };
  
  const confirmDelete = (id, type) => {
    setItemToDelete({ id, type });
    setIsDeleteModalOpen(true);
  };
  
  const navigateToFolder = (folderName) => {
    // Update breadcrumbs without animation
    const newBreadcrumbs = [...breadcrumbs];
    newBreadcrumbs.push({ name: folderName, path: folderName });
    dispatch(updateBreadcrumbs(newBreadcrumbs));
    dispatch(setCurrentFolder(folderName));
  };
  
  const navigateToBreadcrumb = (index) => {
    const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
    dispatch(updateBreadcrumbs(newBreadcrumbs));
    dispatch(setCurrentFolder(newBreadcrumbs[index].path));
  };
  
  const handlePreview = (item) => {
    setPreviewItem(item);
    setIsPreviewOpen(true);
  };
  
  const closePreview = () => {
    setIsPreviewOpen(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };
  
  const getFileIcon = (type) => {
    switch (type) {
      case 'video':
        return (
          <svg className="w-10 h-10 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        );
      case 'photo':
        return (
          <svg className="w-10 h-10 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      default:
        return (
          <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
    }
  };
  
  const getFileUrl = (url) => {
    if (!url) return '';
    
    // If URL is already a full URL
    if (url.startsWith('http')) {
      // Check if it's missing the 'main/' segment and fix if needed
      if (url.includes('r2.dev/safety-content/')) {
        return url.replace('r2.dev/safety-content/', 'r2.dev/main/safety-content/');
      }
      return url;
    }
    
    // For partial URLs, prepend the R2 base URL with main path
    // Make sure we don't duplicate 'main/' if it's already in the url
    const path = url.startsWith('main/') ? url : `main/${url}`;
    return `https://pub-d6f1bdf0d5ad470d9760c07d060062bf.r2.dev/${path}`;
  };
  
  const renderContent = () => {
    if (loading) {
      return <div className="flex justify-center items-center py-16"><Spinner size="large" /></div>;
    }
    
    if (folders.length === 0 && content.length === 0) {
      return (
        <EmptyState
          icon={
            <svg className="w-16 h-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          }
          title="No file storage content found"
          description={isAdmin ? "Upload file storage videos and photos or create folders to organize your content." : "There is no file storage content available yet."}
          action={
            isAdmin && (
              <div className="flex space-x-4">
                <Button onClick={() => setIsUploadModalOpen(true)} color="blue">
                  Upload
                </Button>
                <Button onClick={() => setIsFolderModalOpen(true)} color="gray">
                  Create Folder
                </Button>
              </div>
            )
          }
        />
      );
    }
    
    return (
      <div className="space-y-6">
        {/* Folders */}
        {folders.length > 0 && (
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-3">Folders</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {folders.map(folder => (
                <div 
                  key={folder._id}
                  className="relative group"
                  onClick={() => navigateToFolder(folder.name)}
                >
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 hover:shadow-md transition-shadow duration-200 cursor-pointer h-full flex flex-col">
                    <div className="flex-1 p-4 flex items-center justify-center">
                      <svg className="w-16 h-16 text-blue-100" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19.5 21a3 3 0 003-3V9a3 3 0 00-3-3h-5.379a1.5 1.5 0 01-1.06-.44L10.379 3.44A1.5 1.5 0 009.32 3H4.5a3 3 0 00-3 3v12a3 3 0 003 3h15z" />
                      </svg>
                    </div>
                    <div className="px-4 pb-3 pt-1 text-center">
                      <p className="text-sm font-medium text-gray-800 truncate">{folder.name}</p>
                    </div>
                  </div>
                  
                  {isAdmin && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmDelete(folder._id, 'folder');
                      }}
                      className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    >
                      <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Content items */}
        {content.length > 0 && (
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-3">Files</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {content.map(item => (
                <div key={item._id} className="relative group">
                  <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200 h-full flex flex-col">
                    <div 
                      className="aspect-w-16 aspect-h-12 bg-gray-50 cursor-pointer"
                      onClick={() => handlePreview(item)}
                    >
                      {item.type === 'photo' && (
                        <img 
                          src={getFileUrl(item.fileUrl)} 
                          alt={item.title}
                          className="w-full h-full object-cover" 
                        />
                      )}
                      
                      {item.type === 'video' && (
                        <div className="w-full h-full flex items-center justify-center bg-gray-900 relative">
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center z-10">
                            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        </div>
                      )}
                      
                      {item.type === 'document' && (
                        <div className="w-full h-full flex items-center justify-center">
                          {getFileIcon(item.type)}
                        </div>
                      )}
                    </div>
                    
                    <div className="p-3">
                      <h3 className="text-sm font-medium text-gray-900 truncate">{item.title}</h3>
                      {item.description && (
                        <p className="mt-1 text-xs text-gray-500 line-clamp-2">{item.description}</p>
                      )}
                      <p className="mt-1 text-xs text-gray-400">{new Date(item.createdAt).toLocaleDateString()}</p>
                    </div>
                    
                    <div className="px-3 pb-3 mt-auto">
                      <a 
                        href={getFileUrl(item.fileUrl)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Download
                      </a>
                    </div>
                  </div>
                  
                  {isAdmin && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmDelete(item._id, 'content');
                      }}
                      className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    >
                      <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };
  
  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 w-full">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Safety Resources</h1>
            
            {/* Breadcrumb navigation */}
            <nav className="flex mt-2" aria-label="Breadcrumb">
              <ol className="flex items-center space-x-1">
                {breadcrumbs.map((crumb, index) => (
                  <li key={index} className="flex items-center">
                    {index > 0 && (
                      <svg className="mx-1 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    )}
                    <button
                      className={`text-sm ${index === breadcrumbs.length - 1 ? 'font-medium text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
                      onClick={() => navigateToBreadcrumb(index)}
                    >
                      {crumb.name}
                    </button>
                  </li>
                ))}
              </ol>
            </nav>
          </div>
          
          {isAdmin && (
            <div className="flex space-x-3 mt-4 md:mt-0">
              <Button onClick={() => setIsFolderModalOpen(true)} color="gray">
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                </svg>
                New Folder
              </Button>
              <Button onClick={() => setIsUploadModalOpen(true)} color="blue">
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Upload
              </Button>
            </div>
          )}
        </div>
        
        {renderContent()}
      </div>
      
      {/* Upload Modal */}
      <Modal
        isOpen={isUploadModalOpen}
        onClose={() => {
          setIsUploadModalOpen(false);
          resetUploadForm();
        }}
        title="Upload File Storage Content"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">File</label>
            <input
              type="file"
              onChange={handleFileChange}
              className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              accept="video/*,image/*,.pdf,.doc,.docx"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={fileTitle}
              onChange={(e) => setFileTitle(e.target.value)}
              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              placeholder="Enter title"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
            <textarea
              value={fileDescription}
              onChange={(e) => setFileDescription(e.target.value)}
              rows={3}
              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              placeholder="Enter description"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button 
              color="gray" 
              onClick={() => {
                setIsUploadModalOpen(false);
                resetUploadForm();
              }}
            >
              Cancel
            </Button>
            <Button 
              color="blue" 
              onClick={handleUpload} 
              disabled={!fileToUpload || !fileTitle.trim()}
              loading={loading}
            >
              Upload
            </Button>
          </div>
        </div>
      </Modal>
      
      {/* Create Folder Modal */}
      <Modal
        isOpen={isFolderModalOpen}
        onClose={() => {
          setIsFolderModalOpen(false);
          setFolderName('');
        }}
        title="Create New Folder"
        size="sm"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Folder Name</label>
            <input
              type="text"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              placeholder="Enter folder name"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button 
              color="gray" 
              onClick={() => {
                setIsFolderModalOpen(false);
                setFolderName('');
              }}
            >
              Cancel
            </Button>
            <Button 
              color="blue" 
              onClick={handleCreateFolder} 
              disabled={!folderName.trim()}
              loading={loading}
            >
              Create
            </Button>
          </div>
        </div>
      </Modal>
      
      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setItemToDelete({ id: null, type: null });
        }}
        title="Confirm Delete"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Are you sure you want to delete this {itemToDelete.type === 'folder' ? 'folder' : 'content'}? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3 pt-4">
            <Button 
              color="gray" 
              onClick={() => {
                setIsDeleteModalOpen(false);
                setItemToDelete({ id: null, type: null });
              }}
            >
              Cancel
            </Button>
            <Button 
              color="red" 
              onClick={handleDelete} 
              loading={loading}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
      
      {/* Preview Modal */}
      <Modal
        isOpen={isPreviewOpen}
        onClose={closePreview}
        title={previewItem?.title || "Preview"}
        size="xl"
      >
        <div className="space-y-4">
          {previewItem?.type === 'photo' && (
            <div className="flex justify-center">
              <img
                src={getFileUrl(previewItem.fileUrl)}
                alt={previewItem.title}
                className="max-h-[70vh] max-w-full object-contain rounded-lg"
              />
            </div>
          )}
          
          {previewItem?.type === 'video' && (
            <div className="flex justify-center">
              <video
                ref={videoRef}
                src={getFileUrl(previewItem.fileUrl)}
                className="max-h-[70vh] max-w-full rounded-lg"
                controls
                autoPlay
              />
            </div>
          )}
          
          {previewItem?.type === 'document' && (
            <div className="flex justify-center">
              <iframe
                src={getFileUrl(previewItem.fileUrl)}
                className="w-full h-[70vh] rounded-lg border"
                title={previewItem.title}
              />
            </div>
          )}
          
          {previewItem?.description && (
            <div className="mt-4">
              <h3 className="text-sm font-medium text-gray-700">Description</h3>
              <p className="mt-1 text-sm text-gray-500">{previewItem.description}</p>
            </div>
          )}
          
          <div className="flex justify-end pt-4">
            <a 
              href={getFileUrl(previewItem?.fileUrl)} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="mr-3"
            >
              <Button color="gray">
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download
              </Button>
            </a>
            <Button color="blue" onClick={closePreview}>
              Close
            </Button>
          </div>
        </div>
      </Modal>
    </DashboardLayout>
  );
};

export default Safety; 